import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useStore from '@/components/playbook-builder/hooks/use-store';
import type { Banner } from '../../error-warning-list/error-warning-list';
import type { RulesFormData } from '../rules-node.types';
type FieldPath = `groups.${number}.conditions.${number}.expression`;
type RulesErrorContext = {
  contextType: 'rules_error_context';
  conditionId: string;
  problemMarkers: {
    message: string;
  }[];
};
type ActiveError = {
  conditionId: string;
  fieldPath: FieldPath;
  message: string;
  originalValue: string;
};
const findRulesErrorDetails = (banners: Banner[], nodeId: string) => banners.filter(b => b.nodeId === nodeId).filter(b => b.type === 'error').filter(b => (b.description.context as RulesErrorContext)?.contextType === 'rules_error_context');
const findFieldPath = (formGroups: RulesFormData['groups'], conditionId: string): FieldPath | null => {
  for (let g = 0; g < formGroups.length; g++) {
    const c = formGroups[g].conditions.findIndex(cond => cond.id === conditionId);
    if (c !== -1) return `groups.${g}.conditions.${c}.expression`;
  }
  return null;
};
const getErrorMessage = (context: RulesErrorContext, fallbackMessage: string, defaultMessage: string) => context.problemMarkers?.[0]?.message || fallbackMessage || defaultMessage;
type UseRulesErrorHandlerProps = {
  nodeId: string;
  form: UseFormReturn<RulesFormData>;
  formGroups: RulesFormData['groups'];
};
/**
 * Handles backend validation errors for rules conditions.
 * - Shows each backend error on its condition's form field
 * - Preserves backend errors when form validation clears them on submit
 * - Clears a condition's error when the user edits that field
 */
export const useRulesErrorHandler = t0 => {
  const $ = _c(15);
  const {
    nodeId,
    form,
    formGroups
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rulesDataNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    data: t2
  } = useStore();
  const {
    banners
  } = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const activeErrorsRef = useRef(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Set();
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const dismissedIdsRef = useRef(t4);
  let t5;
  let t6;
  if ($[3] !== banners || $[4] !== form || $[5] !== formGroups || $[6] !== nodeId || $[7] !== t) {
    t5 = () => {
      const errorDetails = findRulesErrorDetails(banners, nodeId);
      if (!errorDetails.length) {
        activeErrorsRef.current = [];
        dismissedIdsRef.current.clear();
        return;
      }
      activeErrorsRef.current = errorDetails.flatMap(errorDetail => {
        const context = errorDetail.description.context as RulesErrorContext;
        const {
          conditionId
        } = context;
        if (dismissedIdsRef.current.has(conditionId)) {
          return [];
        }
        const fieldPath = findFieldPath(formGroups, conditionId);
        if (!fieldPath) {
          return [];
        }
        const message = getErrorMessage(context, errorDetail.description.message, t(_temp));
        return [{
          conditionId,
          fieldPath,
          message,
          originalValue: form.getValues(fieldPath)
        }];
      });
      for (const {
        fieldPath: fieldPath_0,
        message: message_0
      } of activeErrorsRef.current) {
        form.setError(fieldPath_0, {
          type: "manual",
          message: message_0
        });
      }
      const [firstError] = activeErrorsRef.current;
      if (firstError) {
        form.setFocus(firstError.fieldPath);
      }
    };
    t6 = [banners, nodeId, formGroups, form, t];
    $[3] = banners;
    $[4] = form;
    $[5] = formGroups;
    $[6] = nodeId;
    $[7] = t;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== form) {
    t7 = () => {
      const subscription = form.watch((_values, t9) => {
        const {
          name
        } = t9;
        const active = activeErrorsRef.current.find(error => error.fieldPath === name);
        if (!active) {
          return;
        }
        if (form.getValues(active.fieldPath) !== active.originalValue) {
          form.clearErrors(active.fieldPath);
          dismissedIdsRef.current.add(active.conditionId);
          activeErrorsRef.current = activeErrorsRef.current.filter(error_0 => error_0 !== active);
        }
      });
      return () => subscription.unsubscribe();
    };
    t8 = [form];
    $[10] = form;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== form) {
    const reapplyBackendErrors = () => {
      for (const {
        fieldPath: fieldPath_1,
        message: message_1
      } of activeErrorsRef.current) {
        form.setError(fieldPath_1, {
          type: "manual",
          message: message_1
        });
      }
    };
    t9 = {
      reapplyBackendErrors
    };
    $[13] = form;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
function _temp($) {
  return $.conditionInvalid;
}